import moment from 'moment';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useGetRestaurants } from '@common/features/tables/hooks/useGetRestaurants';
import { useGetTablesAvailability } from '@common/features/tables/hooks/useGetTablesAvailability';

import { TableForm } from '../../../components/TableForm/TableForm';
import { DiningOption as DiningOptionType, PackagesFormValues } from '../../../types';
import { filterTimeslotsByMenuOrTimeRestriction } from '../helpers/filterTimeslotsByMenuOrTimeRestrictions';
import { getPromotions } from '../helpers/getPromotions';
import { DiningOptionWrapper } from './DiningOption.styles';
import { usePackageConfiguration } from '../../../contexts';

type DiningOptionProps = {
  option: DiningOptionType;
  index: number;
};

export const DiningOption = ({ option, index }: DiningOptionProps) => {
  const { property, journeyParams } = usePackageConfiguration();

  const { data } = useGetRestaurants(property.id, property.defaultLanguage);
  const restaurants = data?.filter((r) => option.id === r.config.id);
  const { watch, setValue } = useFormContext<PackagesFormValues, any>();

  const adults =
    watch(`tables.restaurants.${option.dayNumber}.${index}.adults`) || journeyParams.adults;
  const children =
    watch(`tables.restaurants.${option.dayNumber}.${index}.children`) || journeyParams.children;
  const visitDate = watch(`tables.restaurants.${option.dayNumber}.${index}.visitDate`);
  const restaurant = watch(`tables.restaurants.${option.dayNumber}.${index}.restaurant`);
  const selectedTimeslot = watch(`tables.restaurants.${option.dayNumber}.${index}.timeslot`);

  const { data: timeslotsData, isFetching: isFetchingTimeslots } = useGetTablesAvailability(
    property.id,
    moment(visitDate).format('YYYY-MM-DD'),
    restaurant?.restaurantName || '',
    restaurant?.id || 0,
    adults,
    children ?? 0
  );

  useEffect(() => {
    if (restaurants?.length === 1 && !restaurant) {
      const { restaurantName, config } = restaurants[0];
      setValue(`tables.restaurants.${option.dayNumber}.${index}.restaurant`, {
        restaurantName,
        configName: config.name,
        id: config.id,
      });
    }
  }, [restaurants]);

  const { timeSlots: timeslots, promotions: promotionsData } = timeslotsData || {
    timeSlots: [],
    promotions: [],
  };

  const filteredTimeSlots = filterTimeslotsByMenuOrTimeRestriction(timeslots, option);

  const promotions = getPromotions(promotionsData, selectedTimeslot as any, option.menuIds);

  return (
    <DiningOptionWrapper>
      <TableForm
        restaurants={restaurants ?? []}
        timeslots={filteredTimeSlots}
        promotions={promotions}
        isFetching={isFetchingTimeslots}
        index={index}
        delayAfter={option.delayAfter || 0}
        dayNumber={option.dayNumber || 0}
        optionName={option.name}
        hasMenuRestrictions={option.menuIds.length === 1}
      />
    </DiningOptionWrapper>
  );
};
